import { SECURITY_EVENT_TYPES } from '../constants';
import logger from '../logger';

/**
 * Runtime Tampering Detector
 * 
 * Detects runtime modifications, code injection, and tampering attempts
 * by monitoring JavaScript environment changes and suspicious behaviors.
 */
export class RuntimeTamperingDetector {
    constructor(onSecurityEvent) {
        this.onSecurityEvent = onSecurityEvent;
        this.initialized = false;
        this.baselineFingerprint = null;
        this.monitoredGlobals = new Set();
        this.originalFunctions = new Map();
        this.appStateHistory = [];
        this.suspiciousPatterns = [];
    }

    /**
     * Initialize the runtime tampering detector
     */
    async initialize() {
        try {
            logger.detector('Runtime Tampering', 'initializing');

            // Create baseline fingerprint of the JavaScript environment
            this.createBaselineFingerprint();

            // Monitor critical global objects
            this.setupGlobalMonitoring();

            // Set up function integrity monitoring
            this.setupFunctionMonitoring();

            // Initialize suspicious pattern detection
            this.initializeSuspiciousPatterns();

            this.initialized = true;
            logger.detector('Runtime Tampering', 'initialized');

        } catch (error) {
            logger.detector('Runtime Tampering', 'failed', error);
            throw error;
        }
    }

    /**
     * Create a baseline fingerprint of the JavaScript environment
     */
    createBaselineFingerprint() {
        try {
            const fingerprint = {
                globalKeys: Object.keys(global || window || {}).sort(),
                consoleKeys: Object.keys(console).sort(),
                dateNow: typeof Date.now,
                mathRandom: typeof Math.random,
                jsonStringify: typeof JSON.stringify,
                jsonParse: typeof JSON.parse,
                setTimeoutType: typeof setTimeout,
                setIntervalType: typeof setInterval,
                timestamp: Date.now()
            };
            
            this.baselineFingerprint = fingerprint;
            logger.verbose('Baseline fingerprint created');
            
        } catch (error) {
            console.warn('RASP - Failed to create baseline fingerprint:', error);
        }
    }

    /**
     * Set up monitoring for critical global objects
     */
    setupGlobalMonitoring() {
        const criticalGlobals = [
            'console',
            'Date',
            'Math',
            'JSON',
            'setTimeout',
            'setInterval',
            'clearTimeout',
            'clearInterval',
            'fetch',
            'XMLHttpRequest'
        ];

        criticalGlobals.forEach(globalName => {
            try {
                const globalObj = global?.[globalName] || window?.[globalName];
                if (globalObj) {
                    this.monitoredGlobals.add(globalName);
                    // Store original reference for integrity checking
                    this.originalFunctions.set(globalName, globalObj);
                }
            } catch (error) {
                console.warn(`RASP - Failed to monitor global ${globalName}:`, error);
            }
        });
    }

    /**
     * Set up function integrity monitoring
     */
    setupFunctionMonitoring() {
        try {
            // Monitor console methods for tampering
            if (console && typeof console === 'object') {
                const originalConsole = { ...console };
                this.originalFunctions.set('console_methods', originalConsole);
            }

            // Monitor critical prototype methods
            const criticalPrototypes = [
                { obj: Array.prototype, methods: ['push', 'pop', 'splice', 'slice'] },
                { obj: Object.prototype, methods: ['toString', 'valueOf'] },
                { obj: Function.prototype, methods: ['call', 'apply', 'bind'] }
            ];

            criticalPrototypes.forEach(({ obj, methods }) => {
                methods.forEach(method => {
                    if (obj && obj[method]) {
                        this.originalFunctions.set(`${obj.constructor.name}.${method}`, obj[method]);
                    }
                });
            });

        } catch (error) {
            // Silently handle function monitoring errors in React Native
            console.log('RASP - Function monitoring limited in this environment');
        }
    }

    /**
     * Initialize suspicious pattern detection (MINIMAL - only obvious attacks)
     */
    initializeSuspiciousPatterns() {
        this.suspiciousPatterns = [
            // Only the most obvious code injection patterns
            /eval\s*\(\s*["'].*['"]\s*\)/gi, // Only eval with string literals

            // Only obvious script injection
            /<script[^>]*>.*malicious.*<\/script>/gi,

            // Reduce false positives by being very specific
        ];
    }

    /**
     * Perform runtime tampering check
     */
    async performCheck() {
        if (!this.initialized) return;

        try {
            // Check environment fingerprint
            await this.checkEnvironmentIntegrity();
            
            // Check function integrity
            await this.checkFunctionIntegrity();
            
            // Check for suspicious global modifications
            await this.checkGlobalModifications();
            
            // Analyze recent app state changes
            await this.analyzeAppStateChanges();
            
        } catch (error) {
            console.warn('RASP - Runtime tampering check failed:', error);
            this.reportSecurityEvent(SECURITY_EVENT_TYPES.RUNTIME_TAMPERING, {
                message: 'Runtime tampering check failed',
                details: { error: error.message }
            });
        }
    }

    /**
     * Check environment fingerprint for changes
     */
    async checkEnvironmentIntegrity() {
        if (!this.baselineFingerprint) return;

        try {
            const currentFingerprint = {
                globalKeys: Object.keys(global || window || {}).sort(),
                consoleKeys: Object.keys(console).sort(),
                dateNow: typeof Date.now,
                mathRandom: typeof Math.random,
                jsonStringify: typeof JSON.stringify,
                jsonParse: typeof JSON.parse,
                setTimeoutType: typeof setTimeout,
                setIntervalType: typeof setInterval
            };

            // Compare fingerprints (LESS AGGRESSIVE - only major changes)
            const changes = this.compareFingerprints(this.baselineFingerprint, currentFingerprint);

            // Only report if there are significant changes (more than 5)
            if (changes.length > 5) {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.RUNTIME_TAMPERING, {
                    message: 'Significant environment fingerprint changes detected',
                    details: { changes: changes.slice(0, 10) } // Limit details
                });
            }

        } catch (error) {
            console.warn('RASP - Environment integrity check failed:', error);
        }
    }

    /**
     * Check function integrity
     */
    async checkFunctionIntegrity() {
        try {
            for (const [name, originalFunction] of this.originalFunctions) {
                let currentFunction;
                
                if (name === 'console_methods') {
                    // Check console methods
                    const currentConsole = { ...console };
                    const originalConsole = originalFunction;
                    
                    for (const method in originalConsole) {
                        if (currentConsole[method] !== originalConsole[method]) {
                            this.reportSecurityEvent(SECURITY_EVENT_TYPES.RUNTIME_TAMPERING, {
                                message: `Console method ${method} has been modified`,
                                details: { method, type: 'console_tampering' }
                            });
                        }
                    }
                    continue;
                }
                
                // Check global functions
                if (name.includes('.')) {
                    // Prototype method
                    const [objName, methodName] = name.split('.');
                    const obj = objName === 'Array' ? Array.prototype :
                               objName === 'Object' ? Object.prototype :
                               objName === 'Function' ? Function.prototype : null;
                    
                    if (obj && obj[methodName]) {
                        currentFunction = obj[methodName];
                    }
                } else {
                    // Global function
                    currentFunction = global?.[name] || window?.[name];
                }
                
                if (currentFunction && currentFunction !== originalFunction) {
                    this.reportSecurityEvent(SECURITY_EVENT_TYPES.RUNTIME_TAMPERING, {
                        message: `Function ${name} has been modified`,
                        details: { functionName: name, type: 'function_tampering' }
                    });
                }
            }

        } catch (error) {
            console.warn('RASP - Function integrity check failed:', error);
        }
    }

    /**
     * Check for suspicious global modifications
     */
    async checkGlobalModifications() {
        try {
            const globalObj = global || window || {};
            const currentGlobals = Object.keys(globalObj);
            
            // Look for new suspicious globals
            const suspiciousGlobals = currentGlobals.filter(key => {
                const value = globalObj[key];
                const keyLower = key.toLowerCase();
                
                // Check for debugging tools
                if (keyLower.includes('debug') || keyLower.includes('devtools')) {
                    return true;
                }
                
                // Check for hooking frameworks
                if (keyLower.includes('hook') || keyLower.includes('intercept')) {
                    return true;
                }
                
                // Check for suspicious function names
                if (typeof value === 'function' && (
                    keyLower.includes('inject') ||
                    keyLower.includes('override') ||
                    keyLower.includes('monkey')
                )) {
                    return true;
                }
                
                return false;
            });

            if (suspiciousGlobals.length > 0) {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.RUNTIME_TAMPERING, {
                    message: 'Suspicious global variables detected',
                    details: { suspiciousGlobals, type: 'global_tampering' }
                });
            }

        } catch (error) {
            console.warn('RASP - Global modifications check failed:', error);
        }
    }

    /**
     * Analyze app state changes for suspicious patterns
     */
    async analyzeAppStateChanges() {
        try {
            // Analyze recent app state transitions
            if (this.appStateHistory.length > 1) {
                const recentChanges = this.appStateHistory.slice(-5);
                
                // Look for rapid state changes (potential tampering)
                const rapidChanges = recentChanges.filter((change, index) => {
                    if (index === 0) return false;
                    const timeDiff = change.timestamp - recentChanges[index - 1].timestamp;
                    return timeDiff < 100; // Less than 100ms between changes
                });

                if (rapidChanges.length > 2) {
                    this.reportSecurityEvent(SECURITY_EVENT_TYPES.RUNTIME_TAMPERING, {
                        message: 'Rapid app state changes detected',
                        details: { rapidChanges: rapidChanges.length, type: 'state_tampering' }
                    });
                }
            }

        } catch (error) {
            console.warn('RASP - App state analysis failed:', error);
        }
    }

    /**
     * Monitor app state changes
     */
    monitorAppStateChange(nextAppState) {
        try {
            this.appStateHistory.push({
                state: nextAppState,
                timestamp: Date.now()
            });

            // Keep only recent history
            if (this.appStateHistory.length > 20) {
                this.appStateHistory = this.appStateHistory.slice(-20);
            }

        } catch (error) {
            console.warn('RASP - App state monitoring failed:', error);
        }
    }

    /**
     * Compare two fingerprints and return differences
     */
    compareFingerprints(baseline, current) {
        const changes = [];

        try {
            // Compare global keys
            const baselineGlobals = new Set(baseline.globalKeys);
            const currentGlobals = new Set(current.globalKeys);
            
            // New globals
            for (const key of currentGlobals) {
                if (!baselineGlobals.has(key)) {
                    changes.push({ type: 'new_global', key });
                }
            }
            
            // Removed globals
            for (const key of baselineGlobals) {
                if (!currentGlobals.has(key)) {
                    changes.push({ type: 'removed_global', key });
                }
            }

            // Type changes
            Object.keys(baseline).forEach(key => {
                if (key !== 'globalKeys' && key !== 'timestamp' && baseline[key] !== current[key]) {
                    changes.push({ type: 'type_change', key, from: baseline[key], to: current[key] });
                }
            });

        } catch (error) {
            console.warn('RASP - Fingerprint comparison failed:', error);
        }

        return changes;
    }

    /**
     * Report a security event
     */
    reportSecurityEvent(eventType, eventData) {
        if (this.onSecurityEvent) {
            this.onSecurityEvent(eventType, eventData);
        }
    }

    /**
     * Cleanup the detector
     */
    cleanup() {
        try {
            this.monitoredGlobals.clear();
            this.originalFunctions.clear();
            this.appStateHistory = [];
            this.initialized = false;
            console.log('RASP - Runtime Tampering Detector cleanup completed');
        } catch (error) {
            console.error('RASP - Runtime Tampering Detector cleanup failed:', error);
        }
    }
}
